import React, { useEffect, useState } from "react";
import { docFileName } from "@/lib/utils";
import api from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Send, SearchCheck, Inbox } from "lucide-react";
import { Link } from "react-router-dom";
import { formatDOP, formatDateOnly } from "@/lib/format";
import { ExpenseFormDialog } from "@/components/expenses/ExpenseFormDialog";
import { EmitPurchaseEcfDialog } from "@/components/EmitPurchaseEcfDialog";
import PrintMenu from "@/components/PrintMenu";
import DocRowActions from "@/components/DocRowActions";
import { downloadEcfXml, hasSignedXml } from "@/lib/docActions";

const TYPE_LABEL = { comprobante: "Comprobante suplidor", E41: "E41 · Informal", E43: "E43 · Gasto menor", E47: "E47 · Exterior" };

function StatusBadge({ s }) {
  if (!s) return null;
  const cls = s === "aceptado" ? "bg-success/15 text-success"
    : s.startsWith("rechazado") || s.startsWith("error") ? "bg-destructive/15 text-destructive" : "bg-muted text-muted-foreground";
  return <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${cls}`} title={s}>{s.split(":")[0]}</span>;
}

export default function Expenses() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [emitFor, setEmitFor] = useState(null);
  const load = async () => setItems((await api.get("/expenses")).data);
  useEffect(() => { load(); }, []);

  const verify = async (e) => {
    try {
      const { data } = await api.post(`/dgii/phase4/refresh-status/${e.ncf}`);
      toast.message(`DGII: ${data.new_status || "desconocido"} · ${e.ncf}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error verificando DGII"); }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Gastos"
        subtitle="Gastos que no tocan el inventario: servicios, reparaciones, oficina, gastos menores (E43), compras a informales (E41) y pagos al exterior (E47)."
        testid="expenses-header"
        actions={<div className="flex gap-2">
          <Link to="/ecf-recibidos"><Button variant="outline" data-testid="expenses-ecf-recibidos-btn"><Inbox className="w-4 h-4 mr-1.5" /> e-CF recibidos</Button></Link>
          <Button onClick={() => setOpen(true)} className="bg-primary font-bold" data-testid="new-expense-btn"><Plus className="w-4 h-4 mr-1.5" /> Nuevo gasto</Button>
        </div>}
      />
      <div className="bg-card border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="expenses-table">
          <thead className="overline border-b border-border">
            <tr>
              <th className="text-left p-3">Número</th><th className="text-left p-3">Tipo</th>
              <th className="text-left p-3">Suplidor / Responsable</th><th className="text-left p-3">Concepto</th>
              <th className="text-left p-3">Fecha</th><th className="text-right p-3">Total</th>
              <th className="text-right p-3">Retenido</th><th className="text-left p-3">Comprobante</th>
              <th className="text-right p-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id} className="border-b border-border/60 hover:bg-accent transition-colors" data-testid={`expense-row-${e.id}`}>
                <td className="p-3 data-cell font-bold">{e.number}</td>
                <td className="p-3 text-xs">{TYPE_LABEL[e.doc_type] || e.doc_type}</td>
                <td className="p-3">{e.supplier_name || e.incurred_by || "—"}</td>
                <td className="p-3 text-xs text-muted-foreground max-w-[220px] truncate">{(e.items || []).map((i) => i.name).join(", ")}</td>
                <td className="p-3 text-xs text-muted-foreground">{e.date ? formatDateOnly(`${e.date}T12:00:00`) : formatDateOnly(e.created_at)}</td>
                <td className="p-3 text-right data-cell font-bold">{formatDOP(e.total)}</td>
                <td className="p-3 text-right data-cell text-xs">{formatDOP((e.itbis_retenido || 0) + (e.isr_retenido || 0))}</td>
                <td className="p-3 text-xs" data-testid={`expense-ncf-${e.id}`}>
                  <span className="font-mono">{e.ncf || e.supplier_ncf || "—"}</span><StatusBadge s={e.ecf_status} />
                </td>
                <td className="p-3 text-right">
                  <DocRowActions
                    printUrl={`/expenses/${e.id}/pdf`}
                    filename={docFileName(e)}
                    onXml={hasSignedXml(e)
                      ? () => downloadEcfXml(`/expenses/${e.id}/ecf-xml`, `${e.ncf || e.number}.xml`) : null}
                    onSend={e.is_electronic ? () => setEmitFor(e) : null}
                    sendTitle={e.ecf_status === "aceptado" ? "Reenviar a DGII" : "Emitir e-CF y enviar a DGII"}
                    onVerify={e.is_electronic && e.ecf_track_id ? () => verify(e) : null}
                    testids={{ print: `expense-pdf-${e.id}`, xml: `expense-xml-${e.id}`, send: `expense-send-ecf-${e.id}`, verify: `expense-verify-ecf-${e.id}` }}
                  />
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-muted-foreground" data-testid="expenses-empty">Sin gastos registrados</td></tr>}
          </tbody>
        </table>
      </div>
      <ExpenseFormDialog open={open} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); setTimeout(load, 6000); }} />
      {emitFor && <EmitPurchaseEcfDialog purchase={emitFor} endpoint="expenses" onClose={() => setEmitFor(null)} onDone={() => { setEmitFor(null); load(); }} />}
    </PageContainer>
  );
}
